/**
 * Popover Block
 * Flexible popover/modal system with multiple trigger types
 */

console.log('[Popover] Script loading...');

class PopoverBlock extends HTMLElement {
  constructor() {
    super();

    this.displayMode = this.dataset.displayMode || 'overlay';
    this.triggerType = this.dataset.triggerType || 'manual';
    this.pageLoadDelay = parseInt(this.dataset.pageLoadDelay) || 0;
    this.animation = this.dataset.animation || 'fade';
    this.autoClose = this.dataset.autoClose === 'true';
    this.autoCloseDuration = parseInt(this.dataset.autoCloseDuration) || 5;
    this.backdropClick = this.dataset.backdropClick === 'true';
    this.escapeKey = this.dataset.escapeKey === 'true';
    this.popoverId = this.dataset.popoverId;
    this.visibilityMode = this.dataset.visibilityMode || 'published_only';

    this.isOpen = false;
    this.autoCloseTimer = null;
    this.triggerElement = null;

    // Bind methods
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleCloseButton = this.handleCloseButton.bind(this);
    this.handleCustomTrigger = this.handleCustomTrigger.bind(this);
  }

  connectedCallback() {
    console.log(`[Popover ${this.popoverId}] Connected to DOM`, {
      triggerType: this.triggerType,
      visibilityMode: this.visibilityMode,
      hidden: this.hidden
    });
    
    this.setupTriggers();
    this.setupEventListeners();

    // If visibility mode is editor_only, mark as open since it's visible by default
    if (this.visibilityMode === 'editor_only' && !this.hidden) {
      this.isOpen = true;
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
    }
  }

  setupTriggers() {
    switch (this.triggerType) {
      case 'page_load':
        this.handlePageLoadTrigger();
        break;
      case 'manual':
      default:
        // Listen for custom trigger events from buttons, forms, etc.
        this.addEventListener('trigger-popover', this.handleCustomTrigger);
        break;
    }
  }

  setupEventListeners() {
    // Backdrop click
    if (this.displayMode === 'overlay' && this.backdropClick) {
      const backdrop = this.querySelector('.popover-block__backdrop');
      if (backdrop) {
        backdrop.addEventListener('click', this.handleBackdropClick);
      }
    }

    // Close button
    const closeButton = this.querySelector('[data-popover-close]');
    if (closeButton) {
      closeButton.addEventListener('click', this.handleCloseButton);
    }

    // Escape key
    if (this.escapeKey) {
      document.addEventListener('keyup', this.handleEscapeKey);
    }
  }

  removeEventListeners() {
    const backdrop = this.querySelector('.popover-block__backdrop');
    if (backdrop) {
      backdrop.removeEventListener('click', this.handleBackdropClick);
    }

    const closeButton = this.querySelector('[data-popover-close]');
    if (closeButton) {
      closeButton.removeEventListener('click', this.handleCloseButton);
    }

    document.removeEventListener('keyup', this.handleEscapeKey);
    this.removeEventListener('trigger-popover', this.handleCustomTrigger);
  }

  handlePageLoadTrigger() {
    console.log(`[Popover ${this.popoverId}] Page load trigger set with ${this.pageLoadDelay}s delay`);
    setTimeout(() => {
      console.log(`[Popover ${this.popoverId}] Opening after ${this.pageLoadDelay}s delay`);
      this.open();
    }, this.pageLoadDelay * 1000);
  }

  handleCustomTrigger(event) {
    const detail = event.detail || {};
    this.triggerElement = detail.triggerElement || null;
    this.open(detail);
  }

  handleBackdropClick(event) {
    if (event.target === event.currentTarget) {
      this.close();
    }
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.close();
    }
  }

  handleCloseButton(event) {
    event.preventDefault();
    this.close();
  }

  open(options = {}) {
    if (this.isOpen) return;

    console.log(`[Popover ${this.popoverId}] Opening...`, {
      hidden: this.hidden,
      visibilityMode: this.visibilityMode,
      triggerType: this.triggerType
    });

    // Dispatch opening event
    const openingEvent = new CustomEvent('popover:opening', {
      detail: { popoverId: this.popoverId, options },
      bubbles: true,
      cancelable: true
    });

    if (!this.dispatchEvent(openingEvent)) {
      return; // Event was cancelled
    }

    this.isOpen = true;

    // Show popover
    this.hidden = false;
    this.style.display = ''; // Clear any display:none from close in editor mode
    this.classList.remove('popover-block--closing');

    // Prevent body scroll for overlay mode
    if (this.displayMode === 'overlay') {
      this.preventBodyScroll();
    }

    // Trap focus for overlay mode
    if (this.displayMode === 'overlay') {
      this.trapFocus();
    }

    // Dispatch opened event
    this.dispatchEvent(new CustomEvent('popover:opened', {
      detail: { popoverId: this.popoverId, options },
      bubbles: true
    }));

    // Setup auto-close
    if (this.autoClose) {
      this.autoCloseTimer = setTimeout(() => {
        this.close();
      }, this.autoCloseDuration * 1000);
    }
  }

  close() {
    if (!this.isOpen) return;

    // Clear auto-close timer
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    // Dispatch closing event
    const closingEvent = new CustomEvent('popover:closing', {
      detail: { popoverId: this.popoverId },
      bubbles: true,
      cancelable: true
    });

    if (!this.dispatchEvent(closingEvent)) {
      return; // Event was cancelled
    }

    this.isOpen = false;

    // Add closing class for animation
    this.classList.add('popover-block--closing');

    // Wait for animation to complete
    const animationDuration = parseFloat(getComputedStyle(this).getPropertyValue('--animation-duration')) || 0.3;

    setTimeout(() => {
      // Handle closing based on visibility mode
      if (this.visibilityMode === 'editor_only') {
        // For editor_only mode, just hide visually (don't use hidden attribute)
        // This keeps the popover available for configuration
        this.style.display = 'none';
      } else {
        // For published_only mode (default), use hidden attribute
        this.hidden = true;
      }

      this.classList.remove('popover-block--closing');

      // Allow body scroll
      if (this.displayMode === 'overlay') {
        this.allowBodyScroll();
      }

      // Remove focus trap
      if (this.displayMode === 'overlay') {
        this.removeFocusTrap();
      }

      // Return focus to trigger element
      if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
        this.triggerElement.focus();
      }

      // Dispatch closed event
      this.dispatchEvent(new CustomEvent('popover:closed', {
        detail: { popoverId: this.popoverId },
        bubbles: true
      }));
    }, animationDuration * 1000);
  }

  preventBodyScroll() {
    document.body.classList.add('popover-open');
    document.body.style.overflow = 'hidden';
  }

  allowBodyScroll() {
    document.body.classList.remove('popover-open');
    document.body.style.overflow = '';
  }

  trapFocus() {
    const focusableElements = this.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length === 0) return;

    this.firstFocusableElement = focusableElements[0];
    this.lastFocusableElement = focusableElements[focusableElements.length - 1];

    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === this.firstFocusableElement) {
          e.preventDefault();
          this.lastFocusableElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === this.lastFocusableElement) {
          e.preventDefault();
          this.firstFocusableElement.focus();
        }
      }
    };

    document.addEventListener('keydown', this.focusTrapHandler);

    // Focus first element
    this.firstFocusableElement.focus();
  }

  removeFocusTrap() {
    if (this.focusTrapHandler) {
      document.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

// Register custom element
if (!customElements.get('popover-block')) {
  console.log('[Popover] Registering custom element...');
  customElements.define('popover-block', PopoverBlock);
  console.log('[Popover] Custom element registered');
}

// Initialize popovers on page load and section load
function initPopovers() {
  const popovers = document.querySelectorAll('popover-block');
  popovers.forEach(popover => {
    // Element will initialize itself via connectedCallback
    // This is just to ensure any lazy-loaded sections work
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPopovers);
} else {
  initPopovers();
}

// Handle Shopify section editor
document.addEventListener('shopify:section:load', (event) => {
  const popovers = event.target.querySelectorAll('popover-block');
  popovers.forEach(popover => {
    // Re-initialize if needed
  });
});

document.addEventListener('shopify:section:unload', (event) => {
  const popovers = event.target.querySelectorAll('popover-block');
  popovers.forEach(popover => {
    if (popover.isOpen) {
      popover.close();
    }
  });
});

// Export for use in other scripts
window.PopoverBlock = PopoverBlock;

/**
 * Global button trigger handler
 * Handles buttons with data-trigger-popover attribute
 */
function setupButtonTriggers() {
  // Use event delegation for dynamically added buttons
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-trigger-popover]');
    if (!button) return;

    const popoverId = button.dataset.triggerPopover;
    if (!popoverId) return;

    e.preventDefault();

    // Find popover by ID
    const popover = document.querySelector(`[data-popover-id="${popoverId}"]`);
    if (popover) {
      popover.dispatchEvent(new CustomEvent('trigger-popover', {
        detail: {
          source: 'button-trigger',
          triggerElement: button
        }
      }));
    } else {
      console.warn(`Popover with ID "${popoverId}" not found`);
    }
  });
}

// Initialize button triggers
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupButtonTriggers);
} else {
  setupButtonTriggers();
}
